import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import {
  ChevronRight,
  ChevronDown,
  CircleHelp,
  Search,
  Truck,
  CreditCard,
  ShieldCheck,
  PackageCheck,
} from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQPage = () => {
  const { language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);

  const isRu = language === 'ru';

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFaqData(response.data.faqs || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqData([
        {
          question: 'Cum pot plasa o comandă?',
          questionRu: 'Как можно оформить заказ?',
          answer:
            'Poți plasa o comandă direct pe site, adăugând produsele dorite în coș și completând formularul de comandă. De asemenea, ne poți contacta telefonic sau prin formularul de contact.',
          answerRu:
            'Вы можете оформить заказ прямо на сайте, добавив нужные товары в корзину и заполнив форму заказа. Также вы можете связаться с нами по телефону или через форму обратной связи.',
        },
        {
          question: 'În cât timp se livrează produsele?',
          questionRu: 'В течение какого времени доставляются товары?',
          answer:
            'Livrarea se efectuează în 24-72 ore, în funcție de localitate și disponibilitatea produselor.',
          answerRu:
            'Доставка осуществляется в течение 24-72 часов, в зависимости от населенного пункта и наличия товара.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const content = {
    title: isRu ? 'Частые вопросы' : 'Întrebări frecvente',
    desc: isRu
      ? 'Мы собрали ответы на самые важные вопросы о заказах, доставке, оплате и товарах.'
      : 'Am adunat răspunsurile la cele mai importante întrebări despre comenzi, livrare, plată și produse.',
    home: isRu ? 'Главная' : 'Acasă',
    page: isRu ? 'Частые вопросы' : 'Întrebări frecvente',

    blockTitle: isRu
      ? 'Нужен быстрый ответ?'
      : 'Ai nevoie rapid de un răspuns?',

    blockDesc: isRu
      ? 'Выберите подходящий вопрос и посмотрите детали. Если не нашли ответ, свяжитесь с нами.'
      : 'Alege întrebarea potrivită și vezi detaliile. Dacă nu găsești răspunsul, contactează-ne.',

    contact: isRu ? 'Связаться' : 'Contactează-ne',
  };

  const quickPoints = [
    {
      icon: Truck,
      title: isRu ? 'Доставка' : 'Livrare',
      desc: isRu ? 'Быстро по всей Молдове' : 'Rapid în toată Moldova',
    },
    {
      icon: CreditCard,
      title: isRu ? 'Оплата' : 'Achitare',
      desc: isRu ? 'Онлайн или при получении' : 'Online sau la primire',
    },
    {
      icon: PackageCheck,
      title: isRu ? 'Заказы' : 'Comenzi',
      desc: isRu ? 'Просто и быстро' : 'Simplu și rapid',
    },
    {
      icon: ShieldCheck,
      title: isRu ? 'Гарантия' : 'Garanție',
      desc: isRu ? 'Проверенные товары' : 'Produse verificate',
    },
  ];

  const renderFaqCard = (item, realIndex) => {
    const isActive = activeIndex === realIndex;
    const question = isRu && item.questionRu ? item.questionRu : item.question;
    const answer = isRu && item.answerRu ? item.answerRu : item.answer;

    return (
      <div
        key={realIndex}
        className={`rounded-[24px] border bg-white overflow-hidden ${
          isActive ? 'border-[#a7cf26]' : 'border-gray-100'
        }`}
      >
        <button
          onClick={() => setActiveIndex(isActive ? null : realIndex)}
          className="w-full flex items-center justify-between gap-4 px-4 md:px-6 py-4 md:py-5 text-left"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={`w-10 h-10 min-w-[40px] rounded-2xl flex items-center justify-center ${
                isActive ? 'bg-[#a7cf26]/10' : 'bg-gray-50'
              }`}
            >
              <CircleHelp
                className={`w-5 h-5 ${
                  isActive ? 'text-[#a7cf26]' : 'text-gray-400'
                }`}
              />
            </div>

            <span className="font-extrabold text-gray-900 text-sm md:text-base leading-snug">
              {question}
            </span>
          </div>

          <div
            className={`w-9 h-9 min-w-[36px] rounded-2xl flex items-center justify-center ${
              isActive ? 'bg-[#a7cf26] text-white' : 'bg-gray-50 text-gray-500'
            }`}
          >
            <ChevronDown
              className={`w-5 h-5 transition-transform duration-300 ${
                isActive ? 'rotate-180' : ''
              }`}
            />
          </div>
        </button>

        <div
          className={`grid transition-all duration-300 ${
            isActive ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="px-4 md:px-6 pb-5 md:pb-6 md:pl-[76px] text-gray-500 leading-7 text-sm">
              {answer}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a7cf26] mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">
            {isRu ? 'Загрузка...' : 'Se încarcă...'}
          </p>
        </div>
      </div>
    );
  }

  const leftColumn = faqData.slice(0, Math.ceil(faqData.length / 2));
  const rightColumn = faqData.slice(Math.ceil(faqData.length / 2));

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={content.title}
        description={content.desc}
        breadcrumbs={[
          { name: content.home, url: '/' },
          { name: content.page, url: '/intrebari-frecvente' },
        ]}
      />

      <div className="bg-white border-b">
        <div className="w-full px-3 md:px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 overflow-x-auto whitespace-nowrap">
            <Link to="/" className="hover:text-[#a7cf26]">
              {content.home}
            </Link>

            <ChevronRight className="w-4 h-4 text-gray-400" />

            <span className="text-gray-900 font-semibold">
              {content.page}
            </span>
          </div>
        </div>
      </div>

      <div className="w-full px-3 md:px-4 py-8">
        <section className="mt-6">
          <div className="mb-6 bg-white border border-gray-100 rounded-[28px] p-5 md:p-7">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-950">
                  {content.blockTitle}
                </h2>

                <p className="mt-2 text-sm md:text-base text-gray-500 max-w-2xl">
                  {content.blockDesc}
                </p>
              </div>

              <div className="hidden md:flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 border border-gray-100">
                <Search className="w-6 h-6 text-[#a7cf26]" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 items-start">
            <div className="space-y-4 md:space-y-5">
              {leftColumn.map((item, index) => renderFaqCard(item, index))}
            </div>

            <div className="space-y-4 md:space-y-5">
              {rightColumn.map((item, index) =>
                renderFaqCard(item, index + leftColumn.length)
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQPage;